if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      /* =========================
         iOS-Scroll-Helfer (neu)
         ========================= */
      isIOS() {
        return /iP(ad|hone|od)/.test(navigator.userAgent)
          || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      }

      restoreScroll(savedY) {
        // double-rAF gegen iPhone-Scroll-Anchoring-Jumps
        requestAnimationFrame(() => {
          window.scrollTo(0, savedY);
          requestAnimationFrame(() => {
            window.scrollTo(0, savedY);
          });
        });
      }

      iosFreezeDuring(fn) {
        if (!this.isIOS()) { fn(); return; }
        const savedY = window.pageYOffset;
        // Scroll-Anchoring temporär aus
        document.documentElement.style.setProperty('overflow-anchor', 'none');
        try {
          fn();
        } finally {
          this.restoreScroll(savedY);
          // Im nächsten Frame wieder freigeben
          requestAnimationFrame(() => {
            document.documentElement.style.removeProperty('overflow-anchor');
          });
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            if (this.isIOS()) {
              this.iosFreezeDuring(() => {
                const currentMain = document.querySelector('main');
                const newMain = html.querySelector('main');
                currentMain.replaceWith(newMain);
              });
            } else {
              HTMLUpdateUtility.viewTransition(
                document.querySelector('main'),
                html.querySelector('main'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          } else {
            if (this.isIOS()) {
              this.iosFreezeDuring(() => {
                const newNode = html.querySelector('product-info');
                this.replaceWith(newNode);
              });
            } else {
              HTMLUpdateUtility.viewTransition(
                this,
                html.querySelector('product-info'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // iOS: Fokussieren triggert Scroll-Jump -> auf iOS gar nicht fokussieren
            const isiOS =
              /iP(ad|hone|od)/.test(navigator.userAgent) ||
              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

            if (isiOS) return;

            // andere Plattformen: ohne Scroll fokussieren
            const el = document.querySelector(`#${targetId}`);
            if (el && typeof el.focus === 'function') {
              try { el.focus({ preventScroll: true }); } catch (e) {}
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant =
          productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      /* =========================
         Preis-Update (Studi .price__container)
         ========================= */
      updatePrice(html) {
        // Quelle bevorzugt im product-info der Antwort suchen
        const sourceRoot =
          html.querySelector(`product-info[id^='MainProduct']`) ||
          html.querySelector('product-info') ||
          html;

        const sourcePrice = sourceRoot.querySelector('.price__container');
        const destPrice = this.querySelector('.price__container');

        if (!sourcePrice || !destPrice) return;

        if (!this.isIOS() && typeof HTMLUpdateUtility?.viewTransition === 'function') {
          const tmp = destPrice.cloneNode(true);
          tmp.innerHTML = sourcePrice.innerHTML;
          HTMLUpdateUtility.viewTransition(
            destPrice,
            tmp,
            this.preProcessHtmlCallbacks
          );
        } else {
          this.iosFreezeDuring(() => {
            destPrice.innerHTML = sourcePrice.innerHTML;
          });
        }
      }

      /* =========================
         Option-Selects ersetzen
         ========================= */
      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects) return;

        if (this.isIOS()) {
          this.iosFreezeDuring(() => {
            this.variantSelectors.replaceWith(variantSelects);
          });
        } else {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          // Preisblock aus Antwort übernehmen (Studi: .price__container)
          this.updatePrice(html);

          // Weitere Bereiche (falls IDs im Theme existieren)
          const copyFromHtml = (id, shouldHide = (src) => false, useTransition = false) => {
            const source =
              html.getElementById(`${id}-${this.sectionId}`) ||
              html.getElementById(`${id}-${this.dataset.section}`);
            const destination =
              this.querySelector(`#${id}-${this.dataset.section}`) ||
              this.querySelector(`#${id}-${this.sectionId}`);

            if (!source || !destination) return;

            const hide = shouldHide(source);

            if (useTransition && !this.isIOS() && typeof HTMLUpdateUtility?.viewTransition === 'function') {
              const tmp = destination.cloneNode(true);
              tmp.innerHTML = source.innerHTML;
              HTMLUpdateUtility.viewTransition(
                destination,
                tmp,
                this.preProcessHtmlCallbacks
              );
              destination.classList.toggle('hidden', hide);
            } else {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', hide);
            }
          };

          copyFromHtml('Sku', ({ classList }) => classList.contains('hidden'));
          copyFromHtml('Inventory', ({ innerText }) => innerText === '');
          copyFromHtml('Volume');
          copyFromHtml('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const items = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const set = new Set(items.map((item) => item.dataset.mediaId));
          const map = new Map(items.map((item, index) => [item.dataset.mediaId, { item, index }]));
          return [items, set, map];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [srcItems, srcSet, srcMap] = refreshSourceData();
          const destItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          const destSet = new Set(destItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = destItems.length - 1; i >= 0; i--) {
            if (!srcSet.has(destItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(destItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < srcItems.length; i++) {
            if (!destSet.has(srcItems[i].dataset.mediaId)) {
              srcItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [srcItems, srcSet, srcMap] = refreshSourceData();

          // re-order to match destination order
          destItems.forEach((destItem, destIndex) => {
            const sourceData = srcMap.get(destItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destIndex + 1})`)
              );
              [srcItems, srcSet, srcMap] = refreshSourceData();
            }
          });
        }

        // set featured media active
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
